import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";
import { getAdminToken, adminLogout } from "../lib/adminAuth";
import {
  Loader2, Plus, Trash2, Save, Send, Calendar, Copy, FlaskConical, Users,
  Megaphone, MailX, Clock, CheckCircle2, FileText, X, Eye, Type, AlignLeft,
  Image as ImageIcon, MousePointerClick, Quote, Minus, ArrowUp, ArrowDown,
  LayoutTemplate, Sparkles, Zap, Mail, MessageSquare, Edit3, RotateCcw,
} from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 9);
const mk = (type, props = {}) => ({ id: uid(), type, ...props });

const blank = () => ({
  id: "new", name: "", subject: "", preheader: "", occasion: "",
  segment: "all", tag: "", status: "draft", stats: {}, plan_id: "", stage: "draft",
  blocks: [mk("heading", { text: "" }), mk("text", { text: "" })],
  body: "", image_url: "", button_label: "", button_url: "",
});

const OCCASIONS = [
  "New Year", "Valentine's", "Spring Renewal", "Mother's Day", "Father's Day",
  "Summer", "Back to School", "Autumn / Gratitude", "Black Friday",
  "Holidays", "Book Launch", "Vault Update", "Monthly Note",
];

// Ready-made starting designs (Zero-Pink: gold, deep blue, charcoal, warm tones)
const STARTERS = [
  {
    key: "reflection", name: "Monthly Reflection Note",
    desc: "A calm personal letter",
    subject: "A quiet note this month",
    blocks: () => [
      mk("heading", { text: "A quiet note this month" }),
      mk("text", { text: "Hello,\n\nThis month I've been sitting with the idea that rest is not a reward for endurance — it is the ground beneath it." }),
      mk("quote", { text: "You do not have to earn your own softness.", attribution: "Braced No More" }),
      mk("text", { text: "Here is one small practice you might carry into your week: when you notice your shoulders climbing, let one slow breath lower them.\n\nTake gentle care." }),
    ],
  },
  {
    key: "launch", name: "Book / Launch Announcement",
    desc: "Cover image, headline, button",
    subject: "Something new from Braced No More",
    blocks: () => [
      mk("image", { url: "", alt: "Cover" }),
      mk("heading", { text: "It's here" }),
      mk("text", { text: "I'm so glad to share this with you. It was made slowly, with you in mind." }),
      mk("button", { label: "Take a look", url: "" }),
    ],
  },
  {
    key: "vault", name: "Vault Update",
    desc: "What's new in the Language Vault",
    subject: "New in the Language Vault",
    blocks: () => [
      mk("heading", { text: "Something new in the Vault" }),
      mk("text", { text: "A new reflection has just been added to the Language Vault — a gentle place to return when words feel hard to find." }),
      mk("button", { label: "Open the Vault", url: "" }),
      mk("divider", {}),
      mk("text", { text: "Return when you're ready — the door stays open." }),
    ],
  },
  {
    key: "seasonal", name: "Seasonal / Occasion Greeting",
    desc: "Warm seasonal note with image",
    subject: "A warm note for this season",
    blocks: () => [
      mk("image", { url: "", alt: "" }),
      mk("heading", { text: "Thinking of you this season" }),
      mk("text", { text: "However this time of year meets you — busy, tender, or somewhere in between — I hope you find a few quiet minutes that are only yours." }),
      mk("quote", { text: "Slow is still forward.", attribution: "" }),
    ],
  },
  {
    key: "simple", name: "Simple Announcement",
    desc: "Headline, one paragraph, button",
    subject: "A quick note",
    blocks: () => [
      mk("heading", { text: "A quick note" }),
      mk("text", { text: "Just one thing I wanted you to know…" }),
      mk("button", { label: "Read more", url: "" }),
    ],
  },
];

const BLOCK_DEFAULTS = {
  heading: { text: "" },
  text: { text: "" },
  image: { url: "", alt: "" },
  button: { label: "Read more", url: "" },
  quote: { text: "", attribution: "" },
  divider: {},
  spacer: {},
};

const BLOCK_META = {
  heading: { label: "Heading", icon: Type },
  text: { label: "Text", icon: AlignLeft },
  image: { label: "Image", icon: ImageIcon },
  button: { label: "Button", icon: MousePointerClick },
  quote: { label: "Quote", icon: Quote },
  divider: { label: "Divider", icon: Minus },
  spacer: { label: "Spacing", icon: Minus },
};

const STATUS_STYLE = {
  draft: "bg-ink/10 text-ink-soft",
  scheduled: "bg-gold/20 text-gold-deep",
  sending: "bg-sage/25 text-ink",
  sent: "bg-sage/30 text-ink",
  failed: "bg-terracotta/20 text-terracotta",
};

function mdInline(text) {
  let esc = (text || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  esc = esc.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  esc = esc.replace(/\[(.+?)\]\((https?:\/\/[^\s)]+)\)/g,
    '<a href="$2" style="color:#9a7b2e;text-decoration:underline">$1</a>');
  return esc;
}

function textToHtml(text) {
  return (text || "").split(/\n\s*\n/).filter((p) => p.trim())
    .map((p) => `<p style="margin:0 0 16px;line-height:1.7;font-size:15px;color:#2C2E33">${mdInline(p).replace(/\n/g, "<br/>")}</p>`)
    .join("");
}

// Preview renderer — mirrors the backend block HTML
function renderBlocks(blocks) {
  return (blocks || []).map((b) => {
    switch (b.type) {
      case "heading":
        return b.text ? `<h2 style="font-family:Georgia,serif;font-size:23px;line-height:1.3;color:#1f2a44;font-weight:600;margin:0 0 16px">${mdInline(b.text)}</h2>` : "";
      case "text":
        return textToHtml(b.text);
      case "image":
        return b.url ? `<img src="${b.url}" alt="${(b.alt || "")}" style="display:block;width:100%;max-width:520px;border-radius:12px;margin:0 0 22px"/>` : `<div style="background:#efeae0;border-radius:12px;height:160px;display:flex;align-items:center;justify-content:center;color:#8A8D91;font-size:13px;margin:0 0 22px">Image — paste a link below</div>`;
      case "button":
        return (b.label && b.url) ? `<p style="margin:26px 0"><span style="background:#1f2a44;color:#F7F5F0;padding:13px 28px;border-radius:999px;font-size:14px;display:inline-block">${mdInline(b.label)}</span></p>` : `<p style="margin:26px 0"><span style="background:#1f2a44;opacity:.4;color:#F7F5F0;padding:13px 28px;border-radius:999px;font-size:14px;display:inline-block">${b.label || "Button"} — add a link</span></p>`;
      case "quote":
        return b.text ? `<blockquote style="margin:0 0 22px;padding:2px 0 2px 20px;border-left:3px solid #9a7b2e;font-style:italic;font-size:18px;line-height:1.6;color:#2C2E33">${mdInline(b.text)}${b.attribution ? `<p style="font-size:13px;color:#8A8D91;margin:10px 0 0">— ${mdInline(b.attribution)}</p>` : ""}</blockquote>` : "";
      case "divider":
        return '<hr style="border:none;border-top:1px solid #e7e2d8;margin:26px 0"/>';
      case "spacer":
        return '<div style="height:26px"></div>';
      default:
        return "";
    }
  }).join("");
}

function normalizeBlocks(r) {
  if (Array.isArray(r.blocks) && r.blocks.length) {
    return r.blocks.map((b) => ({ id: b.id || uid(), ...b }));
  }
  // migrate a legacy text campaign into blocks
  const out = [];
  if (r.image_url) out.push(mk("image", { url: r.image_url, alt: "" }));
  if (r.body) out.push(mk("text", { text: r.body }));
  if (r.button_label && r.button_url) out.push(mk("button", { label: r.button_label, url: r.button_url }));
  return out.length ? out : [mk("heading", { text: "" }), mk("text", { text: "" })];
}

export default function AdminCampaigns() {
  const [token] = useState(() => getAdminToken());
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [rows, setRows] = useState([]);
  const [segments, setSegments] = useState([]);
  const [tags, setTags] = useState([]);
  const [suppressedCount, setSuppressedCount] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [plans, setPlans] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState(blank());
  const [audience, setAudience] = useState({ count: null, sample: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [showPreview, setShowPreview] = useState(true);
  const [schedAt, setSchedAt] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [aiBrief, setAiBrief] = useState("");
  const [aiTone, setAiTone] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiErr, setAiErr] = useState("");
  const [activeTab, setActiveTab] = useState("campaigns");

  const openCampaignById = useCallback(async (cid) => {
    try {
      const { data } = await axios.get(`${API}/admin/campaigns/${cid}`, auth);
      setActiveTab("campaigns");
      setSel(data.id);
      setForm({ ...blank(), ...data, blocks: normalizeBlocks(data) });
      setMsg(""); setErr(""); setSchedAt("");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { setErr("Could not open the template."); }
  }, [auth]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s, t] = await Promise.all([
        axios.get(`${API}/admin/campaigns`, auth),
        axios.get(`${API}/admin/campaign-segments`, auth),
        axios.get(`${API}/admin/campaign-templates`, auth),
      ]);
      setRows(c.data.campaigns || []);
      setSegments(s.data.segments || []);
      setTags(s.data.tags || []);
      setSuppressedCount(s.data.suppressed || 0);
      setTemplates(t.data.templates || []);
      try { const pl = await axios.get(`${API}/admin/marketing-plans`, auth); setPlans(pl.data.plans || []); } catch {}
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr("Could not load campaigns.");
    } finally { setLoading(false); }
  }, [auth]);

  useEffect(() => { if (token) load(); }, [token, load]);

  useEffect(() => {
    const openId = new URLSearchParams(window.location.search).get("open");
    if (openId && rows.length) {
      const r = rows.find((x) => x.id === openId);
      if (r && sel !== openId) openCampaign(r);
    }
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshAudience = useCallback(async (segment, tag) => {
    try {
      const { data } = await axios.get(`${API}/admin/campaign-audience`, {
        ...auth, params: { segment: segment || "all", tag: tag || undefined },
      });
      setAudience(data);
    } catch { setAudience({ count: null, sample: [] }); }
  }, [auth]);

  useEffect(() => {
    if (sel) refreshAudience(form.segment, form.tag);
  }, [sel, form.segment, form.tag, refreshAudience]);

  const openNew = () => { setSel("new"); setForm(blank()); setMsg(""); setErr(""); setShowPreview(true); setSchedAt(""); };
  const openCampaign = (r) => { setSel(r.id); setForm({ ...blank(), ...r, blocks: normalizeBlocks(r) }); setMsg(""); setErr(""); setSchedAt(r.scheduled_at ? toLocalInput(r.scheduled_at) : ""); };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generateCopy = async () => {
    if (!aiBrief.trim()) { setAiErr("Tell the assistant what this campaign is about."); return; }
    setAiBusy(true); setAiErr("");
    try {
      const { data } = await axios.post(`${API}/admin/ai/campaign-copy`,
        { brief: aiBrief.trim(), tone: aiTone.trim() || undefined }, auth);
      setForm((f) => ({
        ...f,
        subject: data.subject || f.subject,
        preheader: data.preheader || f.preheader,
        blocks: [mk("text", { text: data.body || "" })],
      }));
    } catch (e) {
      setAiErr(e.response?.data?.detail || "The writing assistant is unavailable right now.");
    } finally {
      setAiBusy(false);
    }
  };

  // ---- block ops ----
  const addBlock = (type) => setForm((f) => ({ ...f, blocks: [...f.blocks, mk(type, BLOCK_DEFAULTS[type] || {})] }));
  const updateBlock = (id, patch) => setForm((f) => ({ ...f, blocks: f.blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)) }));
  const removeBlock = (id) => setForm((f) => ({ ...f, blocks: f.blocks.filter((b) => b.id !== id) }));
  const moveBlock = (id, dir) => setForm((f) => {
    const i = f.blocks.findIndex((b) => b.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= f.blocks.length) return f;
    const next = [...f.blocks];
    [next[i], next[j]] = [next[j], next[i]];
    return { ...f, blocks: next };
  });

  const applyStarter = (s) => {
    setForm((f) => ({ ...f, subject: f.subject || s.subject, blocks: s.blocks() }));
    setMsg(`Loaded the “${s.name}” design — make it yours.`);
  };

  const payload = () => ({
    name: form.name, subject: form.subject, preheader: form.preheader,
    blocks: form.blocks, segment: form.segment || "all", tag: form.tag || "",
    occasion: form.occasion || "", plan_id: form.plan_id || "",
  });

  const save = async () => {
    setBusy("save"); setErr(""); setMsg("");
    try {
      if (sel === "new") {
        const { data } = await axios.post(`${API}/admin/campaigns`, payload(), auth);
        setRows((r) => [data, ...r]);
        setSel(data.id); setForm((f) => ({ ...f, ...data, blocks: normalizeBlocks({ ...data, blocks: f.blocks }) }));
        setMsg("Draft saved.");
      } else {
        const { data } = await axios.put(`${API}/admin/campaigns/${sel}`, payload(), auth);
        setRows((r) => r.map((x) => (x.id === sel ? data : x)));
        setForm((f) => ({ ...f, ...data, blocks: f.blocks }));
        setMsg("Saved.");
      }
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr(e.response?.data?.detail || "Could not save.");
    } finally { setBusy(""); }
  };

  const sendTest = async () => {
    const email = testEmail.trim();
    if (!email) { setErr("Enter an email to send a test to."); return; }
    if (sel === "new") { setErr("Save the campaign first."); return; }
    setBusy("test"); setErr(""); setMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/campaigns/${sel}/test`, { email }, auth);
      if (data.sent) setMsg(`Test sent to ${email} via ${data.provider}.`);
      else setErr(data.error || "Could not send test.");
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr(e.response?.data?.detail || "Could not send test.");
    } finally { setBusy(""); }
  };

  const sendNow = async () => {
    if (sel === "new") { setErr("Save the campaign first."); return; }
    if (!window.confirm(`Send this campaign now to ${audience.count ?? "all"} recipient(s)? This cannot be undone.`)) return;
    setBusy("send"); setErr(""); setMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/campaigns/${sel}/send`, {}, auth);
      setMsg(`Sending to ${data.recipients} recipient(s)…`);
      setTimeout(load, 1500);
      setTimeout(load, 6000);
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr(e.response?.data?.detail || "Could not send.");
    } finally { setBusy(""); }
  };

  const resendAll = async () => {
    if (sel === "new") return;
    if (form.status === "sending") { setErr("This campaign is already sending."); return; }
    if (!window.confirm(`Resend "${form.name || form.subject || "this campaign"}" to the whole list again (${audience.count ?? "all"} recipient(s))? Anyone who already received it will get it again. It goes out through Mailgun.`)) return;
    setBusy("resend"); setErr(""); setMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/campaigns/${sel}/send`, {}, auth);
      setMsg(`Resending to ${data.recipients} recipient(s) via ${data.provider}…`);
      setTimeout(load, 1500);
      setTimeout(load, 6000);
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr(e.response?.data?.detail || "Could not resend.");
    } finally { setBusy(""); }
  };

  const schedule = async () => {
    if (sel === "new") { setErr("Save the campaign first."); return; }
    if (!schedAt) { setErr("Pick a date and time first."); return; }
    const iso = new Date(schedAt).toISOString();
    setBusy("schedule"); setErr(""); setMsg("");
    try {
      await axios.post(`${API}/admin/campaigns/${sel}/schedule`, { scheduled_at: iso }, auth);
      setRows((r) => r.map((x) => (x.id === sel ? { ...x, status: "scheduled", scheduled_at: iso } : x)));
      setForm((f) => ({ ...f, status: "scheduled", scheduled_at: iso }));
      setMsg(`Scheduled for ${new Date(iso).toLocaleString()}. Sends automatically within ~15 min of that time.`);
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr(e.response?.data?.detail || "Could not schedule.");
    } finally { setBusy(""); }
  };

  const cancelSchedule = async () => {
    setBusy("cancel");
    try {
      await axios.post(`${API}/admin/campaigns/${sel}/cancel`, {}, auth);
      setRows((r) => r.map((x) => (x.id === sel ? { ...x, status: "draft", scheduled_at: null } : x)));
      setForm((f) => ({ ...f, status: "draft", scheduled_at: null }));
      setMsg("Schedule cancelled — back to draft.");
    } catch { setErr("Could not cancel."); } finally { setBusy(""); }
  };

  const duplicate = async (id) => {
    try {
      const { data } = await axios.post(`${API}/admin/campaigns/${id}/duplicate`, {}, auth);
      setRows((r) => [data, ...r]);
      openCampaign(data);
    } catch { setErr("Could not duplicate."); }
  };

  const remove = async () => {
    if (sel === "new") { setSel(null); return; }
    if (!window.confirm("Delete this campaign?")) return;
    try {
      await axios.delete(`${API}/admin/campaigns/${sel}`, auth);
      setRows((r) => r.filter((x) => x.id !== sel));
      setSel(null); setForm(blank());
    } catch { setErr("Could not delete."); }
  };

  const saveTemplate = async () => {
    const name = window.prompt("Name this design:", form.name || "My design");
    if (!name) return;
    try {
      const { data } = await axios.post(`${API}/admin/campaign-templates`, {
        name, subject: form.subject, blocks: form.blocks,
      }, auth);
      setTemplates((t) => [data, ...t]);
      setMsg("Design saved.");
    } catch { setErr("Could not save design."); }
  };

  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    setForm((f) => ({
      ...f, subject: t.subject || f.subject,
      blocks: normalizeBlocks(t),
    }));
    setMsg(`Loaded design “${t.name}”.`);
  };

  const scheduled = rows.filter((r) => r.status === "scheduled")
    .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || ""));
  const isSent = form.status === "sent" || form.status === "sending";
  const previewHtml = useMemo(() => renderBlocks(form.blocks), [form.blocks]);

  return (
    <div className="min-h-[100dvh] bg-ivory-100" data-testid="admin-campaigns">
      <div className="mx-auto max-w-6xl px-5 pt-8 sm:px-8">
        <AdminNav onLogout={adminLogout} />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="inline-flex items-center gap-2 font-display text-3xl text-ink">
              <Megaphone className="h-6 w-6 text-gold-deep" /> Email Campaigns
            </h1>
            <p className="mt-1 text-sm text-ink-soft">
              Compose beautiful notes with visual blocks, start from a ready-made design, and send to your list.
              {suppressedCount > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 text-ink-muted"><MailX className="h-3.5 w-3.5" /> {suppressedCount} unsubscribed</span>
              )}
            </p>
          </div>
          <button onClick={openNew} data-testid="campaign-new" className="debossed-btn inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-display text-ink">
            <Plus className="h-4 w-4" /> New campaign
          </button>
        </div>

        <div className="mt-6 flex gap-2 border-b border-ivory-400/50">
          {[["campaigns", "Campaigns"], ["automations", "Automations"]].map(([key, label]) => (
            <button key={key} onClick={() => setActiveTab(key)} data-testid={`campaigns-tab-${key}`}
              className={`-mb-px rounded-t-lg px-4 py-2 font-display text-sm transition ${activeTab === key ? "border-b-2 border-gold-deep text-ink" : "text-ink-muted hover:text-ink"}`}>
              {label}
            </button>
          ))}
        </div>

        {activeTab === "campaigns" && scheduled.length > 0 && (
          <div className="mt-6 rounded-2xl bg-gold/10 p-4 ring-1 ring-gold/30" data-testid="campaign-planner">
            <p className="flex items-center gap-2 font-display text-sm text-gold-deep"><Calendar className="h-4 w-4" /> Upcoming (scheduled)</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {scheduled.map((r) => (
                <button key={r.id} onClick={() => openCampaign(r)} data-testid={`campaign-upcoming-${r.id}`}
                  className="rounded-full bg-ivory-50 px-3.5 py-1.5 text-left text-xs ring-1 ring-gold/30 hover:ring-gold/60">
                  <span className="font-display text-ink">{r.name || r.subject || "Untitled"}</span>
                  <span className="ml-2 text-ink-muted">{new Date(r.scheduled_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mx-auto max-w-6xl px-5 pb-10 sm:px-8">
        {activeTab === "automations" && (
          <AutomationsPanel token={token} onEditTemplate={openCampaignById} />
        )}
      </div>

      {activeTab === "campaigns" && (
      <div className="mx-auto grid max-w-6xl gap-6 px-5 py-8 sm:grid-cols-[300px_1fr] sm:px-8">
        {/* List */}
        <div className="space-y-2">
          {loading && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-ink-muted" /></div>}
          {!loading && rows.length === 0 && (
            <div className="rounded-xl bg-ivory-50 p-6 text-center ring-1 ring-ivory-400/50">
              <p className="text-sm text-ink-muted">No campaigns yet. Create your first note to your readers.</p>
            </div>
          )}
          {rows.filter((r) => r.origin !== "automation:access_granted").map((r) => (
            <button key={r.id} onClick={() => openCampaign(r)} data-testid={`campaign-row-${r.id}`}
              className={`block w-full rounded-xl p-3 text-left ring-1 transition ${sel === r.id ? "bg-gold/10 ring-gold/40" : "bg-ivory-50 ring-ivory-400/50 hover:ring-gold/30"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="truncate font-display text-ink">{r.name || r.subject || "Untitled"}</span>
                <span className={`flex-none rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide ${STATUS_STYLE[r.status] || "bg-ink/10 text-ink-soft"}`}>{r.status}</span>
              </div>
              <p className="mt-0.5 truncate text-xs text-ink-muted">
                {r.occasion ? `${r.occasion} · ` : ""}{r.subject || "No subject"}
              </p>
              {r.status === "sent" && (
                <p className="mt-1 text-[11px] text-ink-muted">
                  {r.stats?.sent || 0} sent · {r.stats?.opens || 0} opens · {r.stats?.clicks || 0} clicks
                </p>
              )}
            </button>
          ))}
        </div>

        {/* Editor */}
        <div>
          {!sel ? (
            <p className="text-sm text-ink-muted">Select a campaign to edit, or create a new one.</p>
          ) : (
            <div className="rounded-2xl bg-ivory-50 p-6 ring-1 ring-ivory-400/50 sm:p-7" data-testid="campaign-editor">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className={`rounded-full px-3 py-1 text-[11px] uppercase tracking-wide ${STATUS_STYLE[form.status] || "bg-ink/10 text-ink-soft"}`} data-testid="campaign-status">{form.status}</span>
                <div className="flex items-center gap-2">
                  {templates.length > 0 && !isSent && (
                    <select onChange={(e) => e.target.value && applyTemplate(e.target.value)} value="" data-testid="campaign-template-select"
                      className="rounded-full border border-ink/15 bg-white px-3 py-1.5 text-xs text-ink-soft">
                      <option value="">Saved designs…</option>
                      {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  )}
                  <button onClick={() => setShowPreview((p) => !p)} data-testid="campaign-preview-toggle"
                    className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 px-3 py-1.5 text-xs text-ink-soft hover:text-ink">
                    <Eye className="h-3.5 w-3.5" /> {showPreview ? "Hide preview" : "Preview"}
                  </button>
                </div>
              </div>

              {/* Ready-made designs */}
              {!isSent && (
                <div className="mt-5 rounded-xl bg-ivory-100 p-4 ring-1 ring-ivory-400/50" data-testid="campaign-starters">
                  <p className="flex items-center gap-2 text-sm text-ink"><LayoutTemplate className="h-4 w-4 text-gold-deep" /> Start from a design</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {STARTERS.map((s) => (
                      <button key={s.key} onClick={() => applyStarter(s)} data-testid={`campaign-starter-${s.key}`}
                        className="group rounded-xl bg-white px-3.5 py-2 text-left ring-1 ring-ivory-400/60 transition hover:ring-gold/50">
                        <span className="flex items-center gap-1.5 font-display text-sm text-ink"><Sparkles className="h-3.5 w-3.5 text-gold-deep" /> {s.name}</span>
                        <span className="block text-[11px] text-ink-muted">{s.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-5 grid gap-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="text-sm text-ink-soft">Campaign name <span className="text-ink-muted">(internal)</span>
                    <input value={form.name} onChange={(e) => setField("name", e.target.value)} data-testid="campaign-name" disabled={isSent}
                      className="mt-1 w-full rounded-lg border border-ink/15 bg-white px-3 py-2 font-display text-ink disabled:opacity-60" />
                  </label>
                  <label className="text-sm text-ink-soft">Occasion
                    <input list="occasions" value={form.occasion} onChange={(e) => setField("occasion", e.target.value)} data-testid="campaign-occasion" disabled={isSent}
                      placeholder="e.g. Holidays" className="mt-1 w-full rounded-lg border border-ink/15 bg-white px-3 py-2 text-ink disabled:opacity-60" />
                    <datalist id="occasions">{OCCASIONS.map((o) => <option key={o} value={o} />)}</datalist>
                  </label>
                </div>

                <label className="text-sm text-ink-soft">Marketing plan <span className="text-ink-muted">(optional)</span>
                  <select value={form.plan_id || ""} onChange={(e) => setField("plan_id", e.target.value)} data-testid="campaign-plan" disabled={isSent}
                    className="mt-1 w-full rounded-lg border border-ink/15 bg-white px-3 py-2 text-ink disabled:opacity-60">
                    <option value="">No plan</option>
                    {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </label>

                {!isSent && (
                  <div className="rounded-xl bg-gold/10 p-4 ring-1 ring-gold/30" data-testid="campaign-ai">
                    <p className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-wide text-gold-deep"><Sparkles className="h-3.5 w-3.5" /> Write with AI</p>
                    <textarea value={aiBrief} onChange={(e) => setAiBrief(e.target.value)} data-testid="campaign-ai-brief"
                      placeholder="What's this email about? e.g. invite gentle readers into the free Chapter One" rows={2}
                      className="w-full rounded-lg border border-ink/15 bg-white px-3 py-2 text-sm text-ink" />
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <input value={aiTone} onChange={(e) => setAiTone(e.target.value)} data-testid="campaign-ai-tone"
                        placeholder="Tone (optional) — e.g. warm, spacious" className="min-w-[180px] flex-1 rounded-lg border border-ink/15 bg-white px-3 py-2 text-sm text-ink" />
                      <button onClick={generateCopy} disabled={aiBusy} data-testid="campaign-ai-generate"
                        className="debossed-btn inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm text-ink disabled:opacity-50">
                        {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {aiBusy ? "Writing…" : "Draft with Claude"}
                      </button>
                    </div>
                    <p className="mt-2 text-[11px] text-ink-muted">Fills the subject, preheader and a content block. Edit everything freely afterwards.</p>
                    {aiErr && <p className="mt-2 text-sm text-terracotta" data-testid="campaign-ai-error">{aiErr}</p>}
                  </div>
                )}

                <label className="text-sm text-ink-soft">Subject line
                  <input value={form.subject} onChange={(e) => setField("subject", e.target.value)} data-testid="campaign-subject" disabled={isSent}
                    placeholder="What recipients see in their inbox" className="mt-1 w-full rounded-lg border border-ink/15 bg-white px-3 py-2 text-ink disabled:opacity-60" />
                </label>

                {/* Block builder */}
                <div className="rounded-xl bg-white p-4 ring-1 ring-ivory-400/60" data-testid="campaign-blocks">
                  <p className="mb-3 text-[11px] uppercase tracking-wide text-ink-muted">Content blocks</p>
                  <div className="space-y-3">
                    {form.blocks.map((b, i) => (
                      <BlockEditor key={b.id} block={b} index={i} total={form.blocks.length}
                        disabled={isSent}
                        onChange={(patch) => updateBlock(b.id, patch)}
                        onRemove={() => removeBlock(b.id)}
                        onMove={(dir) => moveBlock(b.id, dir)} />
                    ))}
                    {form.blocks.length === 0 && <p className="text-sm text-ink-muted">No blocks yet — add one below or start from a design.</p>}
                  </div>

                  {!isSent && (
                    <div className="mt-4 flex flex-wrap gap-2 border-t border-ivory-400/50 pt-4" data-testid="campaign-add-block-bar">
                      {Object.entries(BLOCK_META).map(([type, m]) => {
                        const Icon = m.icon;
                        return (
                          <button key={type} onClick={() => addBlock(type)} data-testid={`campaign-add-${type}`}
                            className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 px-3 py-1.5 text-xs text-ink-soft transition hover:border-gold/50 hover:text-ink">
                            <Icon className="h-3.5 w-3.5" /> {m.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Audience */}
                <div className="rounded-xl bg-ivory-100 p-4 ring-1 ring-ivory-400/50">
                  <p className="flex items-center gap-2 text-sm text-ink"><Users className="h-4 w-4 text-gold-deep" /> Audience</p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <label className="text-sm text-ink-soft">Segment
                      <select value={form.segment} onChange={(e) => setField("segment", e.target.value)} data-testid="campaign-segment" disabled={isSent}
                        className="mt-1 w-full rounded-lg border border-ink/15 bg-white px-3 py-2 text-ink disabled:opacity-60">
                        {segments.map((s) => <option key={s.type} value={s.type}>{s.label} ({s.count})</option>)}
                      </select>
                    </label>
                    <label className="text-sm text-ink-soft">Tag <span className="text-ink-muted">(optional)</span>
                      <select value={form.tag} onChange={(e) => setField("tag", e.target.value)} data-testid="campaign-tag" disabled={isSent}
                        className="mt-1 w-full rounded-lg border border-ink/15 bg-white px-3 py-2 text-ink disabled:opacity-60">
                        <option value="">Any tag</option>
                        {tags.map((t) => <option key={t.tag} value={t.tag}>{t.tag} ({t.count})</option>)}
                      </select>
                    </label>
                  </div>
                  <p className="mt-3 text-sm text-ink" data-testid="campaign-audience-count">
                    {audience.count == null ? "…" : <><span className="font-display text-lg text-gold-deep">{audience.count}</span> deliverable recipient{audience.count === 1 ? "" : "s"}</>}
                    {audience.sample?.length > 0 && <span className="ml-2 text-xs text-ink-muted">e.g. {audience.sample.slice(0, 3).join(", ")}…</span>}
                  </p>
                </div>

                {showPreview && (
                  <div className="rounded-xl bg-white p-5 ring-1 ring-ivory-400/60" data-testid="campaign-preview">
                    <p className="mb-3 text-[11px] uppercase tracking-wide text-ink-muted">Preview — subject: {form.subject || "(none)"}</p>
                    <div style={{ fontFamily: "Georgia, serif", color: "#2C2E33" }}>
                      <p style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#9a7b2e", margin: "0 0 14px" }}>Braced No More</p>
                      <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                      <p style={{ fontSize: 14, margin: "18px 0 0" }}>With care,<br />Casey Muze</p>
                      <p style={{ fontSize: 11, color: "#8A8D91", marginTop: 20 }}>Includes a one-click unsubscribe link in every send.</p>
                    </div>
                  </div>
                )}
              </div>

              {err && <p className="mt-4 text-sm text-terracotta" data-testid="campaign-error">{err}</p>}
              {msg && <p className="mt-4 text-sm text-gold-deep" data-testid="campaign-msg">{msg}</p>}

              {/* Stats for sent */}
              {form.status === "sent" && (
                <div className="mt-5 grid grid-cols-4 gap-3" data-testid="campaign-stats">
                  {[["Recipients", form.stats?.recipients], ["Sent", form.stats?.sent], ["Opens", form.stats?.opens], ["Clicks", form.stats?.clicks]].map(([k, v]) => (
                    <div key={k} className="rounded-xl bg-ivory-100 p-3 text-center ring-1 ring-ivory-400/50">
                      <p className="font-display text-2xl text-ink">{v ?? 0}</p>
                      <p className="text-xs text-ink-muted">{k}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Resend / test for an already-sent campaign */}
              {isSent && (
                <div className="mt-5 rounded-xl bg-ivory-100 p-4 ring-1 ring-ivory-400/50" data-testid="campaign-resend-box">
                  <p className="flex items-center gap-2 text-sm text-ink"><RotateCcw className="h-4 w-4 text-gold-deep" /> Resend this campaign</p>
                  <p className="mt-1 text-xs text-ink-muted">
                    {form.status === "sending"
                      ? "This campaign is currently sending — watch progress in the Send Log."
                      : `Send the same email to the whole audience again (${audience.count ?? "all"} recipient(s)), through Mailgun. Anyone who already received it will get it again.`}
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <div className="inline-flex items-center gap-2">
                      <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="campaign-resend-test-email" placeholder="you@email.com"
                        className="w-44 rounded-full border border-ink/15 bg-white px-3 py-2 text-sm text-ink" />
                      <button onClick={sendTest} disabled={busy === "test"} data-testid="campaign-resend-test"
                        className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-3.5 py-2 text-sm text-ink-soft hover:text-ink disabled:opacity-50">
                        {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />} Send test
                      </button>
                    </div>
                    <button onClick={resendAll} disabled={busy === "resend" || form.status === "sending"} data-testid="campaign-resend-all"
                      className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-display text-sm text-ivory-50 hover:bg-ink/90 disabled:opacity-50">
                      {busy === "resend" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Resend to whole list
                    </button>
                    <a href="/admin/send-log" data-testid="campaign-resend-sendlog" className="text-sm text-gold-deep underline hover:text-gold">Open Send Log</a>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="mt-6 flex flex-wrap items-center gap-3">
                {!isSent && (
                  <button onClick={save} disabled={busy === "save"} data-testid="campaign-save" className="debossed-btn inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-display text-ink disabled:opacity-50">
                    {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                  </button>
                )}
                {sel !== "new" && !isSent && (
                  <>
                    <div className="inline-flex items-center gap-2">
                      <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="campaign-test-email" placeholder="you@email.com"
                        className="w-40 rounded-full border border-ink/15 bg-white px-3 py-2 text-sm text-ink" />
                      <button onClick={sendTest} disabled={busy === "test"} data-testid="campaign-test-send"
                        className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-3.5 py-2 text-sm text-ink-soft hover:text-ink disabled:opacity-50">
                        {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />} Test
                      </button>
                    </div>
                    <button onClick={sendNow} disabled={busy === "send"} data-testid="campaign-send-now"
                      className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-display text-sm text-ivory-50 hover:bg-ink/90 disabled:opacity-50">
                      {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send now
                    </button>
                    <button onClick={saveTemplate} data-testid="campaign-save-template"
                      className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-3.5 py-2 text-sm text-ink-soft hover:text-ink">
                      <FileText className="h-4 w-4" /> Save as design
                    </button>
                  </>
                )}
                {sel !== "new" && (
                  <button onClick={() => duplicate(sel)} data-testid="campaign-duplicate"
                    className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-3.5 py-2 text-sm text-ink-soft hover:text-ink">
                    <Copy className="h-4 w-4" /> Duplicate
                  </button>
                )}
                <button onClick={remove} data-testid="campaign-delete" className="inline-flex items-center gap-1.5 rounded-full border border-terracotta/40 px-3.5 py-2 text-sm text-terracotta">
                  <Trash2 className="h-4 w-4" /> {sel === "new" ? "Cancel" : "Delete"}
                </button>
              </div>

              {/* Schedule */}
              {sel !== "new" && !isSent && (
                <div className="mt-5 rounded-xl bg-ivory-100 p-4 ring-1 ring-ivory-400/50" data-testid="campaign-schedule-box">
                  <p className="flex items-center gap-2 text-sm text-ink"><Clock className="h-4 w-4 text-gold-deep" /> Schedule for later</p>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <input type="datetime-local" value={schedAt} onChange={(e) => setSchedAt(e.target.value)} data-testid="campaign-schedule-at"
                      className="rounded-lg border border-ink/15 bg-white px-3 py-2 text-sm text-ink" />
                    <button onClick={schedule} disabled={busy === "schedule"} data-testid="campaign-schedule-btn"
                      className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 px-4 py-2 text-sm text-gold-deep disabled:opacity-50">
                      {busy === "schedule" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar className="h-4 w-4" />} Schedule
                    </button>
                    {form.status === "scheduled" && (
                      <button onClick={cancelSchedule} data-testid="campaign-schedule-cancel"
                        className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-4 py-2 text-sm text-ink-soft">
                        <X className="h-4 w-4" /> Cancel schedule
                      </button>
                    )}
                  </div>
                  {form.status === "scheduled" && form.scheduled_at && (
                    <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-gold-deep" data-testid="campaign-scheduled-label">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Scheduled for {new Date(form.scheduled_at).toLocaleString()}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-ink-muted">Scheduled campaigns send automatically within ~15 minutes of the chosen time.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

function AutomationsPanel({ token, onEditTemplate }) {
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [auto, setAuto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/automations`, auth);
      setAuto((data.automations || [])[0] || null);
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr("Could not load automations.");
    } finally { setLoading(false); }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    if (!auto) return;
    setBusy("toggle"); setErr(""); setMsg("");
    try {
      const { data } = await axios.patch(`${API}/admin/automations/${auto.id}`, { enabled: !auto.enabled }, auth);
      setAuto((a) => ({ ...a, enabled: data.enabled }));
      setMsg(data.enabled ? "Automation turned on." : "Automation turned off.");
    } catch { setErr("Could not update the automation."); }
    finally { setBusy(""); }
  };

  const sendTest = async () => {
    setBusy("test"); setErr(""); setMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/automations/${auto.id}/test`, {}, auth);
      const r = data.result || {};
      setMsg(`Test sent to ${data.sent_to} — email ${r.email_status}, text ${r.text_status}.`);
      load();
    } catch (e) { setErr(e.response?.data?.detail || "Could not send the test."); }
    finally { setBusy(""); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-ink-muted" /></div>;
  if (!auto) return <p className="text-sm text-ink-muted">No automations available.</p>;
  const st = auto.stats || {};

  return (
    <div className="max-w-3xl" data-testid="automations-panel">
      {msg && <p className="mb-3 rounded-lg bg-gold/10 px-3 py-2 text-sm text-gold-deep" data-testid="automation-msg">{msg}</p>}
      {err && <p className="mb-3 rounded-lg bg-terracotta/10 px-3 py-2 text-sm text-terracotta" data-testid="automation-err">{err}</p>}

      <div className="rounded-2xl bg-ivory-50 p-5 ring-1 ring-ivory-400/50" data-testid="automation-access-granted">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 rounded-xl bg-gold/15 p-2 text-gold-deep"><Zap className="h-5 w-5" /></span>
            <div>
              <h3 className="font-display text-xl text-ink">{auto.name}</h3>
              <p className="mt-1 max-w-xl text-sm text-ink-soft">{auto.description}</p>
            </div>
          </div>
          <button onClick={toggle} disabled={busy === "toggle"} data-testid="automation-toggle"
            className={`relative inline-flex h-7 w-12 flex-none items-center rounded-full transition ${auto.enabled ? "bg-gold-deep" : "bg-ink/20"}`}
            aria-pressed={auto.enabled} title={auto.enabled ? "On" : "Off"}>
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${auto.enabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>

        <div className="mt-4 grid gap-2 text-sm text-ink-soft sm:grid-cols-2">
          <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-gold-deep" /> Emails the client their access code &amp; list</p>
          <p className="flex items-center gap-2"><MessageSquare className="h-4 w-4 text-gold-deep" /> Texts them {auto.twilio_ready ? "(Twilio ready)" : <span className="text-ink-muted">(texting not set up — skipped)</span>}</p>
          <p className="flex items-center gap-2"><Send className="h-4 w-4 text-gold-deep" /> Confirms to {auto.admin_email}</p>
          <p className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-gold-deep" /> Logs to Send Log &amp; the CRM record</p>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <button onClick={() => onEditTemplate(auto.campaign_id)} data-testid="automation-edit-template"
            className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 px-4 py-2 text-sm text-gold-deep hover:bg-gold/10">
            <Edit3 className="h-4 w-4" /> Edit email template
          </button>
          <button onClick={sendTest} disabled={busy === "test"} data-testid="automation-send-test"
            className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-4 py-2 text-sm text-ink-soft disabled:opacity-50">
            {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />} Send test to me
          </button>
        </div>

        <div className="mt-5 border-t border-ivory-400/50 pt-4 text-xs text-ink-muted">
          <span className="mr-4">Runs: <strong className="text-ink-soft">{st.runs || 0}</strong></span>
          <span className="mr-4">Emailed: <strong className="text-ink-soft">{st.emailed || 0}</strong></span>
          <span className="mr-4">Texted: <strong className="text-ink-soft">{st.texted || 0}</strong></span>
          {auto.last_run_at && <span>Last run: {new Date(auto.last_run_at).toLocaleString()}</span>}
        </div>

        {(auto.recent || []).length > 0 && (
          <div className="mt-4" data-testid="automation-recent">
            <p className="mb-2 font-display text-sm text-ink">Recent</p>
            <div className="space-y-1.5">
              {(auto.recent || []).slice(0, 8).map((r) => (
                <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-ivory-100 px-3 py-2 text-xs ring-1 ring-ivory-400/40">
                  <span className="font-display text-ink">{r.name || r.email || "client"}{r.is_update ? " · updated" : ""}</span>
                  <span className="text-ink-muted">{r.access_list} · email {r.email_status} · text {r.text_status}</span>
                  <span className="text-ink-muted">{r.at ? new Date(r.at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function BlockEditor({ block, index, total, disabled, onChange, onRemove, onMove }) {
  const meta = BLOCK_META[block.type] || { label: block.type, icon: Minus };
  const Icon = meta.icon;
  const inputCls = "w-full rounded-lg border border-ink/15 bg-white px-3 py-2 text-sm text-ink disabled:opacity-60";
  return (
    <div className="rounded-lg bg-ivory-100 p-3 ring-1 ring-ivory-400/50" data-testid={`campaign-block-${index}`}>
      <div className="mb-2 flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-ink-muted"><Icon className="h-3.5 w-3.5 text-gold-deep" /> {meta.label}</span>
        {!disabled && (
          <div className="flex items-center gap-1">
            <button onClick={() => onMove(-1)} disabled={index === 0} data-testid={`campaign-block-up-${index}`} className="rounded p-1 text-ink-muted hover:text-ink disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
            <button onClick={() => onMove(1)} disabled={index === total - 1} data-testid={`campaign-block-down-${index}`} className="rounded p-1 text-ink-muted hover:text-ink disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
            <button onClick={onRemove} data-testid={`campaign-block-remove-${index}`} className="rounded p-1 text-terracotta/70 hover:text-terracotta"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        )}
      </div>

      {block.type === "heading" && (
        <input value={block.text || ""} onChange={(e) => onChange({ text: e.target.value })} disabled={disabled}
          placeholder="Heading text" data-testid={`campaign-block-input-${index}`} className={inputCls} />
      )}
      {block.type === "text" && (
        <textarea value={block.text || ""} onChange={(e) => onChange({ text: e.target.value })} disabled={disabled} rows={4}
          placeholder={"Write here…\n\nBlank line = new paragraph · **bold** · [link](https://…)"} data-testid={`campaign-block-input-${index}`} className={inputCls} />
      )}
      {block.type === "image" && (
        <div className="grid gap-2">
          <input value={block.url || ""} onChange={(e) => onChange({ url: e.target.value })} disabled={disabled}
            placeholder="Image link (paste from Media Library)" data-testid={`campaign-block-input-${index}`} className={inputCls} />
          <input value={block.alt || ""} onChange={(e) => onChange({ alt: e.target.value })} disabled={disabled}
            placeholder="Alt text (optional)" className={inputCls} />
        </div>
      )}
      {block.type === "button" && (
        <div className="grid grid-cols-2 gap-2">
          <input value={block.label || ""} onChange={(e) => onChange({ label: e.target.value })} disabled={disabled}
            placeholder="Button label" data-testid={`campaign-block-input-${index}`} className={inputCls} />
          <input value={block.url || ""} onChange={(e) => onChange({ url: e.target.value })} disabled={disabled}
            placeholder="https://…" className={inputCls} />
        </div>
      )}
      {block.type === "quote" && (
        <div className="grid gap-2">
          <textarea value={block.text || ""} onChange={(e) => onChange({ text: e.target.value })} disabled={disabled} rows={2}
            placeholder="Quote text" data-testid={`campaign-block-input-${index}`} className={inputCls} />
          <input value={block.attribution || ""} onChange={(e) => onChange({ attribution: e.target.value })} disabled={disabled}
            placeholder="Attribution (optional)" className={inputCls} />
        </div>
      )}
      {block.type === "divider" && <p className="text-xs text-ink-muted">A thin horizontal line.</p>}
      {block.type === "spacer" && <p className="text-xs text-ink-muted">Adds vertical breathing room.</p>}
    </div>
  );
}

function toLocalInput(iso) {
  try {
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    const local = new Date(d.getTime() - off * 60000);
    return local.toISOString().slice(0, 16);
  } catch { return ""; }
}
